import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { UncontrolledTooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { connect } from 'react-redux';
import { fileDownload } from '../../helpers/helpers';
import SafeBtn from './SafeBtn';

const DropdownDownloadBtn = ({
  id,
  text,
  tipText,
  url,
  className,
  width,
  loginIdToken,
  onClick,
  dropdownOptions = [], // Array of { text, url, onClick }
}) => {
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  const stopLoading = () => setLoading(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideButton = buttonRef.current && !buttonRef.current.contains(event.target);
      const isOutsideDropdown = !event.target.closest('.custom-dropdown-menu-portal');

      if (isOutsideButton && isOutsideDropdown) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMainClick = () => {
    setLoading(true);
    if (onClick) {
      onClick(() => {
        fileDownload(url, loginIdToken, stopLoading);
      });
      stopLoading();
    } else {
      setLoading(true);
      fileDownload(url, loginIdToken, stopLoading);
    }

    const btn = document.getElementById(id);
    if (btn) {
      btn.blur();
    }
  };

  const handleDropdownToggle = (e) => {
    e.stopPropagation();
    const newState = !dropdownOpen;

    if (newState && buttonRef.current) {
      // Calculate position relative to viewport
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 2,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }

    setDropdownOpen(newState);
  };

  const handleDropdownOptionClick = (option) => {
    setDropdownOpen(false);
    setLoading(true);

    if (option.onClick) {
      option.onClick(() => {
        fileDownload(option.url, loginIdToken, stopLoading);
      });
      stopLoading();
    } else {
      fileDownload(option.url, loginIdToken, stopLoading);
    }
  };

  return (
    <>
      <div ref={buttonRef} style={{ display: 'inline-flex', alignItems: 'stretch' }}>
        {/* Main Download Button */}
        <SafeBtn
          loading={loading}
          id={id}
          className={className}
          style={{
            width: dropdownOptions.length > 0 ? `calc(${width} - 30px)` : width,
            fontSize: '14px',
            borderTopRightRadius: dropdownOptions.length > 0 ? '0' : undefined,
            borderBottomRightRadius: dropdownOptions.length > 0 ? '0' : undefined,
            borderRight: dropdownOptions.length > 0 ? 'none' : undefined,
            whiteSpace: 'nowrap',
          }}
          disabled={!url && !onClick}
          onClick={handleMainClick}
        >
          <FontAwesomeIcon icon={faDownload} />
          &nbsp;&nbsp;
          {text}
        </SafeBtn>

        {/* Dropdown Toggle Button */}
        {dropdownOptions.length > 0 && (
          <SafeBtn
            loading={loading}
            className={className}
            style={{
              width: '35px',
              fontSize: '12px',
              borderTopLeftRadius: '0',
              borderBottomLeftRadius: '0',
              borderLeft: '1px solid var(--orsan-blue)',
              padding: '0',
            }}
            onClick={handleDropdownToggle}
          >
            <FontAwesomeIcon
              icon={faChevronDown}
              style={{
                transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </SafeBtn>
        )}
      </div>

      <UncontrolledTooltip target={id}>{tipText}</UncontrolledTooltip>

      {/* Dropdown Menu using Portal */}
      {dropdownOptions.length > 0 &&
        dropdownOpen &&
        createPortal(
          <div
            className="custom-dropdown-menu-portal"
            style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              minWidth: `${Math.max(dropdownPosition.width, 100)}px`,
              width: 'max-content',
              backgroundColor: '#ffffff',
              border: '1px solid var(--orsan-blue)',
              borderRadius: '4px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 99999,
              maxHeight: '300px',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {dropdownOptions.map((option, index) => (
              <div
                key={index}
                style={{
                  padding: '10px 16px',
                  cursor: 'pointer',
                  borderBottom: index < dropdownOptions.length - 1 ? '1px solid #f1f3f4' : 'none',
                  fontSize: '14px',
                  color: '#212529',
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  transition: 'background-color 0.15s ease',
                  userSelect: 'none',
                  minHeight: '40px',
                  backgroundColor: 'transparent',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
                onClick={() => handleDropdownOptionClick(option)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <FontAwesomeIcon
                  icon={faDownload}
                  style={{ marginRight: '10px', fontSize: '12px', color: '#6c757d' }}
                />
                {option.text}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

DropdownDownloadBtn.propTypes = {
  id: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  tipText: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  loginIdToken: PropTypes.string.isRequired,
  className: PropTypes.string,
  width: PropTypes.string,
  onClick: PropTypes.func,
  dropdownOptions: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      onClick: PropTypes.func,
    }),
  ),
};

DropdownDownloadBtn.defaultProps = {
  className: 'btn-orsan-primary',
  width: '170px',
  onClick: null,
  dropdownOptions: [],
};

const mapStateToProps = (state) => ({
  loginIdToken: state.login.id_token,
});

export default connect(mapStateToProps)(DropdownDownloadBtn);
